import { useAppSelector } from "@/reducers/hooks"
import {
  selectActiveChat,
  selectDeliveryOrder,
  selectDocumentLinkByCmrMessageId,
  selectDocumentLinkByDeliveryOrderId,
  selectDocumentLinkByDeliveryOrderMessageId,
} from "@/reducers/chatsSliceSelectors"
import { selectChatMessages } from "@/reducers/messagesSliceSelectors"
import { type DocumentLink, type Message } from "@/reducers/messagesSlice"
import { gpsDocumentLabel } from "@/lib/gpsDocumentLinks"

type LinkType = "cmr" | "deliveryOrder"

type LinkedFile = {
  url: string
  name?: string
  message?: Message
  /** A GPS link: short "type · date" label, listed inline. */
  gps?: boolean
}

type DocumentLinkResult = {
  documentLinks: DocumentLink[]
  linkedFiles: LinkedFile[]
  linkType: LinkType | undefined
}

const empty: DocumentLinkResult = {
  documentLinks: [],
  linkedFiles: [],
  linkType: undefined,
}

export const useDocumentLink = (message: Message): DocumentLinkResult => {
  const activeChat = useAppSelector(selectActiveChat)
  const chatJid = activeChat.jid
  const chatMessages = useAppSelector(selectChatMessages(chatJid))
  const isCmrRoom = chatJid.startsWith("cmr@automation")

  // CMR lookup: in cmr@automation room, use forwarded message id
  const cmrLink = selectDocumentLinkByCmrMessageId(
    chatMessages,
    isCmrRoom ? message.forwarded?.id : message.id,
  )

  const deliveryOrder = selectDeliveryOrder(chatMessages, message.id)
  const linksByOrder = selectDocumentLinkByDeliveryOrderMessageId(
    chatMessages,
    message.id,
  )
  const linksByOrderInner = selectDocumentLinkByDeliveryOrderMessageId(
    chatMessages,
    deliveryOrder?.id,
  )
  const linksByOrderId = selectDocumentLinkByDeliveryOrderId(
    chatMessages,
    deliveryOrder?.deliveryOrderId,
  )

  // CMR message → link to delivery order
  if (cmrLink) {
    // GPS links carry the order's DB ID; find the message that sent that order.
    const deliveryOrderMessageId =
      cmrLink.deliveryOrderMessageId ||
      Object.values(chatMessages?.messageIdsWithDeliveryOrder ?? {}).find(
        (order) =>
          !!cmrLink.deliveryOrderId &&
          order.deliveryOrderId === cmrLink.deliveryOrderId,
      )?.messageId
    const deliveryOrderMessage = chatMessages?.messages.find(
      (m) => m.id === deliveryOrderMessageId,
    )
    return {
      documentLinks: [cmrLink],
      linkedFiles: [
        {
          url: cmrLink.deliveryOrderFileUrl,
          name: cmrLink.deliveryOrderFileName,
          message: deliveryOrderMessage,
          gps: !!cmrLink.deliveryOrderId,
        },
      ],
      linkType: "deliveryOrder",
    }
  }

  // Delivery order message → links to CMR(s)
  const orderLinks = linksByOrder || linksByOrderInner || linksByOrderId
  if (orderLinks && orderLinks.length > 0) {
    return {
      documentLinks: orderLinks,
      linkedFiles: orderLinks.map((link) => {
        const cmrMessage = chatMessages?.messages.find(
          (m) => m.id === link.cmrMessageId,
        )
        return {
          url: link.cmrFileUrl,
          name: link.deliveryOrderId
            ? gpsDocumentLabel(link, cmrMessage?.timestamp)
            : link.cmrFileName,
          message: cmrMessage,
          gps: !!link.deliveryOrderId,
        }
      }),
      linkType: "cmr",
    }
  }

  return empty
}
